import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pipelinesApi } from '@/lib/api';

export function usePipelines() {
  return useQuery({
    queryKey: ['pipelines'],
    queryFn: pipelinesApi.list,
  });
}

export function usePipeline(id: string | undefined) {
  return useQuery({
    queryKey: ['pipelines', id],
    queryFn: () => pipelinesApi.getById(id!),
    enabled: !!id,
  });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) => pipelinesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}

export function useUpdatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; defaultOwnerId?: string | null } }) =>
      pipelinesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelinesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}

export function useSetDefaultPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelinesApi.setDefault(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}

export function useCreateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, data }: { pipelineId: string; data: Parameters<typeof pipelinesApi.createStage>[1] }) =>
      pipelinesApi.createStage(pipelineId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useUpdateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageId, data }: {
      pipelineId: string;
      stageId: string;
      data: Parameters<typeof pipelinesApi.updateStage>[2];
    }) => pipelinesApi.updateStage(pipelineId, stageId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useDeleteStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageId }: { pipelineId: string; stageId: string }) =>
      pipelinesApi.deleteStage(pipelineId, stageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}

export function useReorderStages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageIds }: { pipelineId: string; stageIds: string[] }) =>
      pipelinesApi.reorderStages(pipelineId, stageIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
}
